import React, { useState } from 'react';
import {Mail, MapPin, Phone, Clock, Facebook, Instagram, MessageCircleDashed as MessageCircle} from 'lucide-react';
import { lumi } from '../lib/lumi';
import { useLanguage } from '../contexts/LanguageContext';

export default function Contact() {
  const { t } = useLanguage();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    postalCode: '',
    service: '',
    message: ''
  });
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState<{ type: 'success' | 'error', message: string } | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setStatus(null);

    try {
      await lumi.functions.invoke('sendContactEmail', {
        method: 'POST',
        body: formData
      });

      setStatus({ type: 'success', message: t('contact.formSuccess') });
      setFormData({ name: '', email: '', phone: '', postalCode: '', service: '', message: '' });
    } catch (error: any) {
      const errorMessage = error?.data?.error || t('contact.formError');
      setStatus({ type: 'error', message: errorMessage });
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="contact" className="py-24 bg-green-900 text-white relative overflow-hidden">
      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-10 pointer-events-none">
        <svg className="h-full w-full" viewBox="0 0 100 100" preserveAspectRatio="none">
          <path d="M0 100 C 20 0 50 0 100 100 Z" fill="currentColor" />
        </svg>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
          
          {/* Contact Info */}
          <div>
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-playfair font-bold mb-6">{t('contact.title')}</h2>
            <p className="text-green-100 text-base sm:text-lg mb-10 leading-relaxed">{t('contact.description')}</p>

            <div className="space-y-8">
              <div className="flex items-start gap-4">
                <div className="p-3 rounded-lg bg-white/10 backdrop-blur-sm">
                  <MapPin className="h-6 w-6 text-green-300" />
                </div>
                <div>
                  <h3 className="font-semibold text-lg mb-1">{t('contact.location')}</h3>
                  <p className="text-green-100">{t('contact.locationValue')}</p>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="p-3 rounded-lg bg-white/10 backdrop-blur-sm">
                  <Mail className="h-6 w-6 text-green-300" />
                </div>
                <div>
                  <h3 className="font-semibold text-lg mb-1">{t('contact.email')}</h3>
                  <a href="mailto:pinhacorada@gmail.com" className="text-green-100 hover:text-white transition-colors">
                    pinhacorada@gmail.com
                  </a>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="p-3 rounded-lg bg-white/10 backdrop-blur-sm">
                  <Phone className="h-6 w-6 text-green-300" />
                </div>
                <div>
                  <h3 className="font-semibold text-lg mb-1">{t('contact.phone')}</h3>
                  <a href="tel:+351965812210" className="text-green-100 hover:text-white transition-colors">
                    965 812 210
                  </a>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="p-3 rounded-lg bg-white/10 backdrop-blur-sm">
                  <MessageCircle className="h-6 w-6 text-green-300" />
                </div>
                <div>
                  <h3 className="font-semibold text-lg mb-1">{t('contact.whatsapp')}</h3>
                  <a href="https://wa.me/351967905983" target="_blank" rel="noopener noreferrer" className="text-green-100 hover:text-white transition-colors">
                    967 905 983
                  </a>
                </div>
              </div>

              <div>
                <h3 className="font-semibold text-lg mb-4">{t('contact.socialMedia')}</h3>
                <div className="flex gap-4">
                  <a href="https://www.facebook.com/pinhacorada" target="_blank" rel="noopener noreferrer" className="p-3 rounded-lg bg-white/10 backdrop-blur-sm hover:bg-white/20 transition-all transform hover:scale-110">
                    <Facebook className="h-6 w-6 text-green-300" />
                  </a>
                  <a href="https://www.instagram.com/pinhacorada" target="_blank" rel="noopener noreferrer" className="p-3 rounded-lg bg-white/10 backdrop-blur-sm hover:bg-white/20 transition-all transform hover:scale-110">
                    <Instagram className="h-6 w-6 text-green-300" />
                  </a>
                </div>
              </div>
            </div>
          </div>

          {/* Contact Form */}
          <div className="bg-white rounded-2xl p-8 md:p-10 shadow-2xl text-gray-900">
            <h3 className="text-2xl font-bold mb-6 font-playfair">{t('contact.formTitle')}</h3>
            
            {status && (
              <div className={`mb-6 p-4 rounded-lg ${status.type === 'success' ? 'bg-green-100 text-green-800 border border-green-200' : 'bg-red-100 text-red-800 border border-red-200'}`}>
                {status.message}
              </div>
            )}

            <form className="space-y-6" onSubmit={handleSubmit}>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">{t('contact.formName')}</label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="w-full px-4 py-3 rounded-lg bg-gray-50 border border-gray-200 focus:border-green-500 focus:ring-2 focus:ring-green-200 outline-none transition-all"
                    placeholder={t('contact.formName')} />

                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">{t('contact.formPhone')}</label>
                  <input
                    type="tel"
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    className="w-full px-4 py-3 rounded-lg bg-gray-50 border border-gray-200 focus:border-green-500 focus:ring-2 focus:ring-green-200 outline-none transition-all"
                    placeholder={t('contact.formPhone')} />

                </div>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">{t('contact.formEmail')}</label>
                  <input
                    type="email"
                    required
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    className="w-full px-4 py-3 rounded-lg bg-gray-50 border border-gray-200 focus:border-green-500 focus:ring-2 focus:ring-green-200 outline-none transition-all"
                    placeholder="email@exemplo.com" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">{t('contact.formPostalCode')}</label>
                  <input
                    type="text"
                    value={formData.postalCode}
                    onChange={(e) => setFormData({ ...formData, postalCode: e.target.value })}
                    className="w-full px-4 py-3 rounded-lg bg-gray-50 border border-gray-200 focus:border-green-500 focus:ring-2 focus:ring-green-200 outline-none transition-all"
                    placeholder="0000-000" />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">{t('contact.formService')}</label>
                <input
                  type="text"
                  value={formData.service}
                  onChange={(e) => setFormData({ ...formData, service: e.target.value })}
                  className="w-full px-4 py-3 rounded-lg bg-gray-50 border border-gray-200 focus:border-green-500 focus:ring-2 focus:ring-green-200 outline-none transition-all"
                  placeholder={t('contact.formServicePlaceholder')} />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">{t('contact.formMessage')}</label>
                <textarea
                  rows={4}
                  required
                  value={formData.message}
                  onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                  className="w-full px-4 py-3 rounded-lg bg-gray-50 border border-gray-200 focus:border-green-500 focus:ring-2 focus:ring-green-200 outline-none transition-all resize-none"
                  placeholder={t('contact.formMessagePlaceholder')}>
                </textarea>
              </div>

              <button
                type="submit"
                disabled={loading}
                className="w-full py-4 rounded-lg bg-green-800 text-white font-bold hover:bg-green-900 transition-all transform hover:scale-[1.02] shadow-lg disabled:opacity-50 disabled:cursor-not-allowed">

                {loading ? t('contact.formSending') : t('contact.formSubmit')}
              </button>
            </form>
          </div>
        </div>
      </div>
    </section>);

}